import classNames from "classnames";
import * as React from "react";

import {
  ResourceView,
  useResourceView,
  type Filter,
} from "@/shared/components/ResourceView";
import type {
  BedrockCatalogModel,
  OrgModelConfigSummary,
  OrgModelConfigSummaryModel,
} from "@/shared/domain/orgs";
import {
  useSearchOrgModelConfigIds,
  useSearchOrgModelConfigs,
} from "@/shared/domain/orgs/hooks";
import {
  describeModelSource,
  isRobotoManaged,
} from "@/shared/domain/orgs/orgModelConfig";
import { RobotoButton } from "@/shared/foundation/primitives";
import {
  DataTable,
  createColumnHelper,
  deriveLoadingState,
  type ColDef,
  type ICellRendererParams,
} from "@/shared/foundation/primitives/Table";
import { userFacingErrorMessage } from "@/shared/http/userFacingErrorMessage";

import styles from "./AiModels.module.css";
import {
  orgModelConfigFilterDefinitions,
  toModelConfigFilter,
} from "./orgModelConfigFilters";

/** The bulk endpoint's per-request cap. */
export const MAX_BULK_ORGS = 1000;

const columnHelper = createColumnHelper<OrgModelConfigSummary>();

const COLUMNS: ColDef<OrgModelConfigSummary>[] = [
  columnHelper.accessor("org_name", {
    headerName: "Org",
    flex: 2,
    minWidth: 200,
    hide: false,
    cellRenderer: (params: ICellRendererParams<OrgModelConfigSummary>) => {
      const row = params.data;
      if (!row) {
        return null;
      }
      return (
        <span className={styles.orgCell}>
          <span className={styles.orgName}>{row.org_name}</span>
          <span className={styles.orgId}>{row.org_id}</span>
        </span>
      );
    },
  }),
  {
    colId: "source",
    headerName: "Models from",
    flex: 1,
    minWidth: 160,
    hide: false,
    sortable: false,
    // Without the Roboto default to compare against, this says only where
    // the models come from: Roboto, the org's own connections, or both.
    valueGetter: (params) =>
      params.data ? describeModelSource(params.data.models, undefined) : "",
  },
  {
    colId: "models",
    headerName: "Models (default in bold)",
    flex: 3,
    minWidth: 260,
    hide: false,
    sortable: false,
    cellRenderer: (params: ICellRendererParams<OrgModelConfigSummary>) => {
      const row = params.data;
      if (!row) {
        return null;
      }
      return (
        <span className={styles.models}>
          {row.models.map((model) => (
            <ModelChip key={model.id} model={model} />
          ))}
        </span>
      );
    },
  },
  columnHelper.accessor("modified", {
    headerName: "Modified",
    flex: 1,
    minWidth: 120,
    hide: false,
    valueFormatter: (params) =>
      params.value ? String(params.value).substring(0, 10) : "Never",
  }),
];

const NO_FILTERS: Filter[] = [];

/**
 * One model on an org's list: its label (bold for the default), then a
 * marker for each role or restriction it has: the default, the utility
 * model, on one of the org's own connections, kept out of the chat picker,
 * or switched off.
 */
function ModelChip({ model }: { model: OrgModelConfigSummaryModel }) {
  const own = !isRobotoManaged(model);
  const markers = [
    model.is_default && "default",
    model.is_utility && "utility",
    own && "own",
    !model.in_picker && "not in picker",
    model.enabled === false && "off",
  ].filter((marker): marker is string => marker !== false);
  return (
    <span
      title={model.model_id}
      className={classNames(styles.modelChip, {
        [styles.modelChipDefault]: model.is_default,
        [styles.modelChipOwn]: own,
      })}
    >
      {model.label}
      {markers.map((marker) => (
        <span key={marker} className={styles.modelChipMarker}>
          {marker}
        </span>
      ))}
    </span>
  );
}

interface OrgModelConfigTableProps {
  /** The catalog's chat models, which the "Has model" filter offers. */
  chatModels: readonly BedrockCatalogModel[];
  /**
   * Actions for the targeted orgs, rendered in the selection bar. Called
   * with the ids of the ticked rows, or of every org matching the filters
   * after "Select all N matching", and a function that clears the target.
   */
  renderActions: (
    targetIds: string[],
    clearTarget: () => void,
  ) => React.ReactNode;
  /** Makes the table shorter where it shares the page, e.g. under the catalog. */
  compact?: boolean;
  /** Adds an "Edit models" action to each row, called with that org's row. */
  onEditOrg?: (row: OrgModelConfigSummary) => void;
}

/** The per-row "Edit models" action column. */
function editColumn(
  onEditOrg: (row: OrgModelConfigSummary) => void,
): ColDef<OrgModelConfigSummary> {
  return {
    colId: "edit",
    headerName: "",
    width: 130,
    sortable: false,
    hide: false,
    cellRenderer: (params: ICellRendererParams<OrgModelConfigSummary>) => {
      const row = params.data;
      if (!row) {
        return null;
      }
      return (
        <RobotoButton variant="ghost" size="sm" onClick={() => onEditOrg(row)}>
          Edit models
        </RobotoButton>
      );
    },
  };
}

/**
 * Every org's chat model list (Roboto-managed and own-connection models),
 * filtered and paged on the server (`POST /v1/orgs/llm-providers/search`)
 * through the shared
 * ResourceView filter menu, with row selection and "Select all N matching".
 * Used by the Orgs view and by the catalog's "Make available to orgs" panel.
 */
export function OrgModelConfigTable({
  chatModels,
  renderActions,
  compact = false,
  onEditOrg,
}: OrgModelConfigTableProps) {
  const view = useResourceView<OrgModelConfigSummary>({
    getRowId: (row) => row.org_id,
  });
  const { clearSelection } = view.table;

  const columns = React.useMemo(
    () =>
      onEditOrg === undefined ? COLUMNS : [...COLUMNS, editColumn(onEditOrg)],
    [onEditOrg],
  );

  const filterDefinitions = React.useMemo(
    () => orgModelConfigFilterDefinitions(chatModels),
    [chatModels],
  );

  const structured =
    view.filters.type === "structured" ? view.filters.filters : NO_FILTERS;
  const translation = React.useMemo(
    () => toModelConfigFilter(structured),
    [structured],
  );
  const { filter, matchesNothing } = translation;

  // The search ANDs its fields, so an OR match mode cannot be honored; snap
  // the toggle back rather than let it claim a search that is not run.
  const matchMode =
    view.filters.type === "structured" ? view.filters.matchMode : "AND";
  const { setMatchMode } = view;
  React.useEffect(() => {
    if (matchMode === "OR") {
      setMatchMode("AND");
    }
  }, [matchMode, setMatchMode]);

  // "Select all N matching" resolves ids for the filters it was asked
  // under; any filter change makes the answer stale, so it is dropped.
  const [allMatching, setAllMatching] = React.useState<{
    filters: typeof view.filters;
    ids: string[];
  } | null>(null);
  const allMatchingIds =
    allMatching !== null && allMatching.filters === view.filters
      ? allMatching.ids
      : null;

  const searchQuery = useSearchOrgModelConfigs(
    filter,
    view.table.currentPage,
    view.table.pageSize,
  );
  const searchIds = useSearchOrgModelConfigIds();

  const rows = matchesNothing ? [] : (searchQuery.data ?? []);
  const totalCount = matchesNothing ? 0 : searchQuery.totalCount;
  const targetIds =
    allMatchingIds ?? view.table.selection.map((row) => row.org_id);
  const tooMany = targetIds.length > MAX_BULK_ORGS;

  const clearTarget = React.useCallback(() => {
    setAllMatching(null);
    clearSelection();
  }, [clearSelection]);

  return (
    <div className={styles.orgTable}>
      <ResourceView.Root>
        <ResourceView.Actions>
          <ResourceView.Filters
            filterDefinitions={filterDefinitions}
            onFilterSelected={view.commit}
          />
        </ResourceView.Actions>
        <ResourceView.ActiveFilters
          filters={view.filters}
          filterDefinitions={filterDefinitions}
          onRemove={view.removeFilter}
          onUpdate={view.updateFilter}
          onClear={view.clearFilters}
          onMatchModeChange={view.setMatchMode}
          onFilterSelected={view.commit}
        />
        {translation.unsupported.map((message) => (
          <p key={message} className={styles.note} role="status">
            {message}
          </p>
        ))}

        <div className={styles.selectionBar}>
          <span>
            {allMatchingIds !== null
              ? `All ${allMatchingIds.length} matching orgs selected`
              : `${targetIds.length} selected`}
          </span>
          {allMatchingIds === null && totalCount !== undefined && (
            <RobotoButton
              variant="ghost"
              size="sm"
              loading={searchIds.isPending}
              disabled={totalCount === 0}
              onClick={() => {
                const filters = view.filters;
                searchIds.mutate(filter, {
                  onSuccess: (ids) => setAllMatching({ filters, ids }),
                });
              }}
            >
              {`Select all ${totalCount} matching`}
            </RobotoButton>
          )}
          {targetIds.length > 0 && (
            <RobotoButton variant="ghost" size="sm" onClick={clearTarget}>
              Clear selection
            </RobotoButton>
          )}
          {!tooMany && renderActions(targetIds, clearTarget)}
          {tooMany && (
            <span className={styles.note}>
              At most {MAX_BULK_ORGS} orgs at a time; narrow the filters.
            </span>
          )}
          {searchIds.isError && (
            <span className={styles.error} role="alert">
              {userFacingErrorMessage(searchIds.error)}
            </span>
          )}
        </div>

        {searchQuery.error && (
          <p className={styles.error} role="alert">
            {userFacingErrorMessage(searchQuery.error)}
          </p>
        )}

        {/* DataTable renders no rows unless an ancestor has a definite height. */}
        <div
          className={classNames(styles.tableFrame, {
            [styles.tableFrameCompact]: compact,
          })}
        >
          <DataTable<OrgModelConfigSummary>
            table={view.table}
            columns={columns}
            data={rows}
            hasNextPage={!matchesNothing && searchQuery.hasNextPage}
            loading={deriveLoadingState({
              isLoading: searchQuery.isLoading,
              isFetching: searchQuery.isFetching,
            })}
            totalRowCount={totalCount}
            noRowsContent="No orgs match the current filters"
            selectionEnabled
          />
        </div>
      </ResourceView.Root>
    </div>
  );
}
